import {UiServiceV2} from '@gongxigames/framework/slotV2/baseClasses/uiServiceV2/UIServiceV2';
import {FeatureEventData, slotStore} from '@gongxigames/framework/slotV2/globals';
import {Timer} from '@gongxigames/framework/core';
import {uiTickerStore} from '@gongxigames/framework/slotV2/ui/services/UITicker/uiTickerStore';
import {COLOR_HIGHLIGHT, COLOR_HIGHLIGHT_GREEN} from '@gongxigames/framework/slotV2/ui/ui-koala/CONSTANTS';
import {Pay, PayType} from '@gongxigames/framework/slotV2/types/LogicData';

const languageService = (id: string, textValues?: Record<string, string | number>): string => {
    return slotStore.languageService.value?.get(id, textValues) || '';
};

export class UITickerService extends UiServiceV2 {
    public static NAME = 'UITickerService';

    protected idleLangKeys = ['pressSpin', 'spinToWin', 'timeToBet', 'playing'];
    protected idleLangIndex = 0;
    protected isGameStarted = false;
    protected idleTimer?: Timer;
    protected waitForAcceptance = false;
    protected waitForSpinning = false;
    protected isIdleAvailable = true;

    public onSetupEvents(): void {
        this.emitter.on('Game.command.UITicker.show', this.onUITickerShow.bind(this));
        this.emitter.on('Game.command.UITicker.scatterPay', this.onScatterPay.bind(this));
        this.emitter.on('Game.command.UITicker.lineWin', this.setLineWinTickerText.bind(this));
        this.emitter.on('Game.command.UITicker.waysWin', this.setWaysWinTickerText.bind(this));
        this.emitter.on('Game.command.UITicker.freeGameBonusWin', this.onFreeSpinsFinishedInitialAnimation.bind(this));
        this.emitter.on('Game.command.UITicker.setFreeSpinState', this.onSetFreeSpinState.bind(this));
        this.emitter.on('Game.command.UITicker.toggleIdle', this.onToggleIdle.bind(this));
        this.emitter.on('Game.command.UITicker.setIdleLangKeys', this.onSetIdleLangKeys.bind(this));
        this.emitter.on('Game.command.UITicker.setHighlightColor', this.onSetHighlightColor.bind(this));

        this.emitter.on('UI.command.totalBetChanged', this.onTotalBetChanged.bind(this));
        this.emitter.on('General.Game.STARTED', this.onGameStarted.bind(this));
        this.emitter.on('General.BigWin.STARTED', this.onBigWinStarted.bind(this));
        this.emitter.on('General.WinPresentation.PLAY_ALL_WIN_SYMBOLS', this.onInitialBase.bind(this));
        this.emitter.on('General.WinPresentation.PLAY_ALL_WIN_SYMBOLS_IN_FS', this.onInitialFreeSpins.bind(this));

        this.emitter.on('General.Reels.StartSpinning', this.onReelsSpinningStarted.bind(this));
        this.emitter.on('General.Reels.AllReelsStopped', this.onAllReelsStopped.bind(this));

        this.emitter.on('Game.command.round.featureStarted', this.onFeatureStarted.bind(this));
        this.emitter.on('Game.command.round.featureAccepted', this.onFeatureAccepted.bind(this));
        this.emitter.on('Game.command.round.featureStopped', this.onFeatureStopped.bind(this));
    }

    protected onSetHighlightColor(color: number): void {
        this.setColor(color);
    }

    protected onSetIdleLangKeys(keys: string[]): void {
        this.idleLangKeys = keys;
    }

    protected onToggleIdle(value: boolean): void {
        this.isIdleAvailable = value;
    }

    protected onUITickerShow(str: string): void {
        uiTickerStore.text.value = str;
    }

    protected onSetFreeSpinState(state: boolean): void {
        uiTickerStore.uiTicker_isFreeSpinsState.value = state;
    }

    protected onFeatureStopped(data: FeatureEventData): void {
        if (data.type === 'freeSpins') {
            this.waitForSpinning = true;
        }
        this.isIdleAvailable = slotStore.logic_nextActionIsSpin.value;
    }

    protected onFeatureStarted(data: FeatureEventData): void {
        if (data.type === 'freeSpins') {
            this.waitForAcceptance = true;
        }
        this.isIdleAvailable = false;
        this.stopIdleTimer();
        this.textValue = '';
    }

    protected onFeatureAccepted(): void {
        if (this.waitForAcceptance) {
            this.waitForAcceptance = false;
            uiTickerStore.uiTicker_isFreeSpinsState.value = true;
        }
    }

    protected onGameStarted(): void {
        this.isGameStarted = true;
        this.setTickerValue();
    }

    set textValue(value: string) {
        this.stopIdleTimer();
        uiTickerStore.text.value = value || '';
    }

    protected setTickerValue(isIdle = true): void {
        if (!slotStore.logic_canSpin.value && !uiTickerStore.uiTicker_isFreeSpinsState.value) {
            this.textValue = languageService('lowBalance');
        } else {
            if (slotStore.isAutoplay.value || !slotStore.logic_nextActionIsSpin.value) {
                this.textValue = languageService('goodLuck');
            } else if (isIdle && this.isIdleAvailable) {
                this.runIdleTimer();
            } else {
                this.textValue = '';
            }
        }
    }

    protected updateTicker(): void {
        const key = this.idleLangKeys[this.idleLangIndex];

        this.setColor(key === 'playing' ? COLOR_HIGHLIGHT : COLOR_HIGHLIGHT_GREEN);
        uiTickerStore.text.value = languageService(key);
        this.idleLangIndex = (this.idleLangIndex + 1) % this.idleLangKeys.length;
    }

    protected stopIdleTimer(): void {
        this.idleLangIndex = 0;
        this.idleTimer?.stop();
        this.idleTimer = undefined;
        this.setColor(COLOR_HIGHLIGHT);
    }

    protected setColor(color: number): void {
        if (uiTickerStore.isDifferentColorPossible.value) {
            uiTickerStore.color.value = color;
        }
    }

    protected runIdleTimer(): void {
        this.updateTicker();

        this.idleTimer?.stop();
        this.idleTimer = undefined;
        this.idleTimer = slotStore.updateManager.value!.addTimer(this.runIdleTimer.bind(this), uiTickerStore.idleTimerDelay.value, true);
    }

    protected onFreeSpinsFinishedInitialAnimation(): void {
        this.textValue = languageService('freeGameBonusWin', this.getWinObject(this.getFreeGamesWin()));
    }

    public getFreeGamesWin(): number {
        const pays = slotStore.logic_sessionForFreeSpinsWinningRound.value.pays;
        let noFreeGamesWin = 0;

        pays.forEach((pay) => {
            if (pay.type !== PayType.SCATTER) {
                noFreeGamesWin += pay.win * pay.bet;
            }
        });

        return slotStore.logic_totalWin.value - noFreeGamesWin;
    }

    protected onInitialFreeSpins(): void {
        this.textValue = slotStore.logic_win.value ? languageService('freeGamePay', this.getWinObject(slotStore.logic_win.value)): '';
    }

    protected setLineWinTickerText([lineNr, win, multiplier]: [lineNr: string, win: number, multiplier?: number]): void {
        const translationKey = multiplier ? 'linePayMultiplier': 'linePay';

        this.textValue = languageService(translationKey, {lineNr, ...this.getWinObject(win), multiplier: multiplier || '' });
    }

    protected setWaysWinTickerText([waysNr, win, multiplier]: [waysNr: number, win: number, multiplier?: number]): void {
        const translationKey = multiplier ? 'winPayMultiplier': waysNr > 1 ? 'winPays' : 'winPay';

        this.textValue = languageService(translationKey, {waysNr, ...this.getWinObject(win), multiplier: multiplier || ''});
    }

    protected onScatterPay(pay: Pay): void {
        if (pay.win === 0) {
            return;
        }
        const win = pay.win * pay.bet;

        this.textValue = languageService('scatterPay', this.getWinObject(win));
    }

    protected onReelsSpinningStarted(): void {
        if (this.waitForSpinning) {
            this.waitForSpinning = false;
            uiTickerStore.uiTicker_isFreeSpinsState.value = false;
        }
        this.textValue = languageService('goodLuck');
    }

    protected onAllReelsStopped(): void {
        this.setTickerValue(slotStore.logic_win.value <= 0);
    }

    protected onTotalBetChanged(): void {
        if (!this.isGameStarted) {
            return;
        }
        this.stopIdleTimer();
        this.setTickerValue();
    }

    protected onBigWinStarted(): void {
        this.textValue = '';
    }

    protected onInitialBase(): void {
        if (!slotStore.logic_isBigWin.value) {
            const {logic_previousActionIsPick, logic_totalWin, logic_win} = slotStore.values();
            const win = logic_previousActionIsPick ? logic_totalWin : logic_win;

            this.textValue = win > 0 ? languageService('gamePay', this.getWinObject(win)) : '';
        }
    }

    protected getWinObject(win: number): {win: string;} {
        return {win: slotStore.currencyFormatter.value?.formatCents(win) || ''};
    }
}